/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Stack } from '@mui/material';
import React from 'react';
import { CustomToggleButton } from '@opengeoweb/shared';
import { Sources, Layers } from '@opengeoweb/theme';
import { SoundingsMarkersSwitch } from './SoundingsMarkersSwitch';
import { useSoundingsTranslation } from '../../utils/i18n';

export enum MenuOptions {
  soundingsManager = 'Sd Manager',
  sources = 'sources',
  locations = 'locations',
}
interface SoundingsMenuBarProps {
  handleClick: (menuType: MenuOptions) => void;
  isOpen: Record<MenuOptions, boolean>;
  selectedSourcesAmount?: number;
  shouldHideMarkers: boolean;
  toggleMarkers: (shouldHideMarkers: boolean) => void;
}
const buttonStyle = {
  height: '10px !important',
  width: '130px',
};

export const SoundingsMenuBar = ({
  handleClick,
  isOpen,
  selectedSourcesAmount = 0,
  shouldHideMarkers,
  toggleMarkers,
}: SoundingsMenuBarProps): React.ReactElement => {
  const { t } = useSoundingsTranslation();
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        marginBottom: '4px',
      }}
    >
      <CustomToggleButton
        data-testid="soundingsManagerButton"
        onClick={() => handleClick(MenuOptions.soundingsManager)}
        selected={isOpen[MenuOptions.soundingsManager]}
        size="small"
        sx={buttonStyle}
        variant="tool"
      >
        <Layers />
        {MenuOptions.soundingsManager}
      </CustomToggleButton>

      <CustomToggleButton
        data-testid="soundingsSourcesButton"
        onClick={() => handleClick(MenuOptions.sources)}
        selected={isOpen[MenuOptions.sources]}
        size="small"
        sx={buttonStyle}
        variant="tool"
      >
        <Sources sx={{ marginRight: '4px!important' }} />
        {selectedSourcesAmount
          ? `${t('sources')} (${selectedSourcesAmount})`
          : t('sources')}
      </CustomToggleButton>
      <CustomToggleButton
        data-testid="soundingsLocationsButton"
        onClick={() => handleClick(MenuOptions.locations)}
        selected={isOpen[MenuOptions.locations]}
        size="small"
        sx={buttonStyle}
        variant="tool"
      >
        {t('locations')}
      </CustomToggleButton>
      <SoundingsMarkersSwitch
        shouldHideMarkers={shouldHideMarkers}
        toggleMarkers={toggleMarkers}
      />
    </Stack>
  );
};
